import { IPatient } from '@alberta/konexi-shared';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SelectionItem } from 'src/app/shared/models/selectionItem';

import { ModalController } from '@ionic/angular';
import { BarcodeScanningModalComponent } from '../../barcode/barcode-scan-modal-component';
import { ExtendedBarcode } from '../../barcode/barcode-scanning.service';
import { CrossTherapyArticleLineViewModel } from './../../../../../../../business/cross-therapy/cross-therapy-article-line-view-model';
import { SelectionService } from './../../../../../services/selection.service';

@Component({
  selector: 'itl-cross-therapy-document-article-data',
  templateUrl: './cross-therapy-document-article-data.component.html',
  styleUrls: ['./cross-therapy-document-article-data.component.scss'],
})
export class CrossTherapyDocumentArticleDataComponent implements OnInit {
  @Input()
  articleLine: CrossTherapyArticleLineViewModel;
  @Input()
  patient: IPatient;
  @Input()
  showScanButtons: boolean;
  @Input()
  public showIdentificationNumbers: boolean;
  @Input()
  public showStorageLocation: boolean;
  @Output()
  // tslint:disable-next-line: max-line-length
  public articleLineChanged: EventEmitter<CrossTherapyArticleLineViewModel> =
    new EventEmitter<CrossTherapyArticleLineViewModel>();

  public resmedTenantOptions: SelectionItem[] = [new SelectionItem('RMD', 'RMD'), new SelectionItem('RMT', 'RMT')];
  public unitSelectionItems: SelectionItem[] = [];
  public operatingHoursLength = 6;
  public scanSerialNumberError: string | null = null;
  public scanBatchNumberError: string | null = null;

  constructor(
    private _selectionService: SelectionService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _translate: TranslateService,
    private _modalCtrl: ModalController
  ) {}

  ngOnInit() {
    if (!this.articleLine.resmedTenant && this.patient) {
      this.articleLine.resmedTenant = this.patient.resmedTenant;
    }
  }

  public tryToParseBarcode(barcode: string, type: 'serialNumber' | 'batchNumber') {
    this.scanSerialNumberError = null;
    this.scanBatchNumberError = null;
    if (!barcode) {
      return;
    }

    const regex = /[\)\(](\d{2})[=\)]([^\(\)\=]+)/g;

    const gs1Data = [];
    let match;

    while ((match = regex.exec(barcode)) !== null) {
      gs1Data.push({ ai: match[1], data: match[2] });
    }

    if (gs1Data.length === 0) {
      if (type === 'serialNumber') {
        this.articleLine.serialNumber = barcode;
      } else {
        this.articleLine.batchNumber = barcode;
      }
      this._changeDetectorRef.detectChanges();
      this.emitArticleLineChanged();
      return;
    }
    if (type === 'serialNumber') {
      const serialNumber = gs1Data.find(item => item.ai === '21')?.data;

      if (!serialNumber) {
        this.articleLine.serialNumber = '';
        this.scanSerialNumberError = 'Ungültiger Barcode oder Barcode enthält keine Seriennummer';
      }
      this.articleLine.serialNumber = serialNumber;
    } else {
      const batchNumber = gs1Data.find(item => item.ai === '10')?.data;

      if (!batchNumber) {
        this.articleLine.batchNumber = '';
        this.scanBatchNumberError = 'Ungültiger Barcode oder Barcode enthält keine Chargennummer';
      }
      this.articleLine.batchNumber = batchNumber;
    }
    this._changeDetectorRef.detectChanges();
    this.emitArticleLineChanged();
  }

  public emitArticleLineChanged() {
    this.scanSerialNumberError = null;
    this.scanBatchNumberError = null;
    this.articleLineChanged.emit(this.articleLine);
  }

  public increaseQuantity() {
    this.articleLine.quantity++;
    this.emitArticleLineChanged();
  }

  public decreaseQuantity() {
    if (this.articleLine.quantity > 0) {
      this.articleLine.quantity--;
      this.emitArticleLineChanged();
    }
  }
  public async openUnitSelect() {
    this.unitSelectionItems = [];
    if (this.articleLine.article.packagingUnits) {
      this.articleLine.article.packagingUnits
        .filter(packagingUnit => !packagingUnit.archived)
        .forEach(packagingUnit => {
          this.unitSelectionItems.push(
            new SelectionItem(packagingUnit._id, `${packagingUnit.unit} (${packagingUnit.quantity})`)
          );
        });
    }
    const result = await this._selectionService.openSelection(
      this.articleLine.packagingUnit._id,
      this.unitSelectionItems,
      this._translate.instant('order.edit.unitSelect')
    );
    if (!result.canceled) {
      this.articleLine.packagingUnitId = result.value;
      this.articleLine.packagingUnit = this.articleLine.article.packagingUnits.find(
        packagingUnit => packagingUnit._id === result.value
      );
      this.articleLine.packagingUnitQuantity = this.articleLine.packagingUnit.quantity;
      this._changeDetectorRef.markForCheck();
    }
  }

  public async scanSerialNumber() {
    this.scanSerialNumberError = null;
    const serialNumber = await this.scanBarcode('serialNo');
    if (!serialNumber) {
      this.scanSerialNumberError = 'Ungültiger Barcode oder Barcode enthält keine Seriennummer';
    }
    this.articleLine.serialNumber = serialNumber;
    this._changeDetectorRef.markForCheck();
    this.emitArticleLineChanged();
  }

  public async scanBatchNumber() {
    this.scanBatchNumberError = null;
    const batchNumber = await this.scanBarcode('batchNo');
    if (!batchNumber) {
      this.scanBatchNumberError = 'Ungültiger Barcode oder Barcode enthält keine Chargennummer';
    }
    this.articleLine.batchNumber = batchNumber;
    this._changeDetectorRef.markForCheck();
    this.emitArticleLineChanged();
  }

  private async scanBarcode(type: 'serialNo' | 'batchNo'): Promise<string> {
    const scanDialogue = await this._modalCtrl.create({
      component: BarcodeScanningModalComponent,
      // Set `visibility` to `visible` to show the modal (see global.scss)
      cssClass: 'barcode-scanning-modal',
      showBackdrop: false,
      componentProps: {
        parseGS1: true,
        // you could set certain formats or lensFacing (front/back) here
        // formats: formats,
        // lensFacing: lensFacing,
      },
    });

    await scanDialogue.present();
    const retVal = await scanDialogue.onDidDismiss();

    if (!retVal.data?.barcode) {
      return '';
    }

    const barcode: ExtendedBarcode = retVal.data.barcode;
    if (barcode.isGs1Barcode) {
      switch (type) {
        case 'serialNo':
          return barcode.gs1Data.find(item => item.ai === '21')?.data ?? retVal.data?.barcode?.displayValue;
        case 'batchNo':
          return barcode.gs1Data.find(item => item.ai === '10')?.data ?? retVal.data?.barcode?.displayValue;
        default:
          return retVal.data?.barcode?.displayValue;
      }
    }
    return retVal.data?.barcode?.displayValue;
  }
}
